/**
 * Detailer: quote builder.
 *
 * Customers pick products from a list, enter their details, and either print
 * the quote to PDF or send it to the store.
 *
 * WHAT THIS DOES AND DOES NOT DO
 *
 * Download is fully client-side: the quote is composed into a print document
 * and handed to the browser's print dialogue, where "Save as PDF" is a
 * standard destination on every current desktop and mobile browser. No
 * library, no network, no backend.
 *
 * Email is a real form POST to Shopify's contact endpoint, so the STORE
 * receives the quote immediately. Shopify cannot mail a generated PDF to the
 * customer from a theme, that needs a server. `data-quote-endpoint` on the
 * root element is the seam for that: set it and the payload below is POSTed
 * there as JSON instead, leaving the rest of this file unchanged.
 *
 * SELECTION SURVIVES PAGINATION. The catalogue is paginated server-side, so
 * selections are held in sessionStorage keyed by variant id and rehydrated on
 * load. Without that, paging to find a second product would silently discard
 * the first.
 */

const STORAGE_KEY = 'detailer:quote:v2';
const CONTACT_ACTION = '/contact';

/** @typedef {{qty:number,title:string,variant:string,sku:string,price:number,url:string,custom?:boolean}} Line */

class DetailerQuote extends HTMLElement {
  /** @type {Map<string, Line>} */
  #lines = new Map();

  /** @type {AbortController | null} */
  #controller = null;

  /** Which panel is showing, 1-3. */
  #step = 1;

  /** Monotonic id source for custom lines, so removing one never lets the next
   *  addition reuse a live key. */
  #seq = 0;

  /** Stable quote reference, generated once and persisted, so the number on the
   *  screen, the printed PDF and the emailed copy all agree. */
  #ref = '';

  /** @type {ReturnType<typeof setTimeout> | null} */
  #searchTimer = null;

  /** @type {AbortController | null} */
  #searchController = null;

  /** Pristine first-page catalogue markup, kept so clearing the search box can
   *  put the paginated list back without a reload. */
  #catalogueHTML = null;

  connectedCallback() {
    this.#restore();

    const { signal } = (this.#controller = new AbortController());

    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('input', this.#handleInput, { signal });
    this.addEventListener('submit', this.#handleSubmit, { signal });

    const list = this.querySelector('[data-quote-list]');
    if (list instanceof HTMLElement) this.#catalogueHTML = list.innerHTML;

    this.#syncRows();
    this.#renderSteps();
    this.#render();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    if (this.#searchTimer) clearTimeout(this.#searchTimer);
    this.#searchController?.abort();
  }

  get #currency() {
    return this.dataset.currency || '';
  }

  /** Money formatting mirrors Liquid's `money` filter closely enough for a
   *  printed quote: the shop's own formatted strings are used for each line,
   *  and only the total is computed here. */
  #money(cents) {
    const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // The shop's own `{{ 0 | money }}` output is the source of truth for
    // affixes, so "R 0.00", "$0.00" and "0,00 kr" all round-trip without this
    // file knowing anything about the store's locale.
    const zero = this.dataset.moneyZero || '0.00';
    const first = zero.search(/\d/);
    const last = zero.search(/\d(?!.*\d)/);
    const prefix = first > -1 ? zero.slice(0, first) : '';
    const suffix = last > -1 ? zero.slice(last + 1) : '';
    return `${prefix}${amount}${suffix}`;
  }

  /**
   * The catalogue paginates server-side, so every page turn is a full reload.
   * Selections, the customer's details AND the current step all have to survive
   * it, or paging for a second product would drop the customer back to a blank
   * step 1.
   */
  #restore() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return;

      for (const [id, line] of Object.entries(parsed.lines || {})) {
        if (line && typeof line.qty === 'number' && line.qty > 0) {
          this.#lines.set(id, line);
          const match = /^custom:(\d+)$/.exec(id);
          if (match) this.#seq = Math.max(this.#seq, Number(match[1]));
        }
      }

      for (const [name, value] of Object.entries(parsed.customer || {})) {
        const field = this.querySelector(`[data-quote-field][name="${name}"]`);
        if (field instanceof HTMLInputElement && typeof value === 'string') field.value = value;
      }

      if (typeof parsed.reference === 'string') this.#ref = parsed.reference;

      // Clamp rather than trust: a stored step 3 with an emptied quote would
      // otherwise strand the customer on a panel they cannot act on.
      const step = Number(parsed.step) || 1;
      this.#step = step === 3 && this.#lines.size === 0 ? 2 : Math.min(3, Math.max(1, step));
    } catch (error) {
      // A corrupt or unavailable store must never take the page down; the
      // customer simply starts with an empty quote.
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }

  #persist() {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          lines: Object.fromEntries(this.#lines),
          customer: this.#customer(),
          step: this.#step,
          reference: this.#ref,
        })
      );
    } catch (error) {
      /* storage full or blocked, the quote still works for this page view */
    }
  }

  /** Reflects stored state back onto whichever rows are on this page. */
  #syncRows() {
    for (const row of this.querySelectorAll('[data-quote-row]')) {
      const id = this.#variantId(row);
      const line = id ? this.#lines.get(id) : null;
      const toggle = row.querySelector('[data-quote-toggle]');
      const qty = row.querySelector('[data-quote-qty]');
      if (toggle instanceof HTMLInputElement) toggle.checked = Boolean(line);
      if (qty instanceof HTMLInputElement) qty.value = String(line ? line.qty : 1);
      row.classList.toggle('is-selected', Boolean(line));
    }
  }

  /** @param {Element} row */
  #variantId(row) {
    const select = row.querySelector('[data-quote-variant]');
    if (select instanceof HTMLSelectElement) return select.value;
    return row.getAttribute('data-variant-id') || '';
  }

  /** @param {Element} row */
  #readRow(row) {
    const select = row.querySelector('[data-quote-variant]');
    const option = select instanceof HTMLSelectElement ? select.selectedOptions[0] : null;
    const qtyInput = row.querySelector('[data-quote-qty]');
    const qty = qtyInput instanceof HTMLInputElement ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;

    return {
      qty,
      title: row.getAttribute('data-title') || '',
      variant: option ? option.dataset.variantTitle || '' : row.getAttribute('data-variant-title') || '',
      sku: option ? option.dataset.sku || '' : row.getAttribute('data-sku') || '',
      price: Number(option ? option.dataset.price : row.getAttribute('data-price')) || 0,
      url: row.getAttribute('data-url') || '',
    };
  }

  /**
   * SEARCH SPANS THE WHOLE CATALOGUE, NOT THE CURRENT PAGE.
   *
   * The list is paginated server-side (37 pages of it), so filtering the rows
   * already in the DOM only ever searched 1/37th of the shop: typing "kit"
   * on page 1 reported nothing found. Instead this queries Shopify's own search
   * index through templates/search.quote.liquid, which returns the same row
   * markup as a bare fragment, and swaps it into the list.
   *
   * @param {string} raw
   */
  #queueSearch(raw) {
    if (this.#searchTimer) clearTimeout(this.#searchTimer);
    const term = raw.trim();
    // Debounced so a fast typist issues one request, not one per keystroke.
    this.#searchTimer = setTimeout(() => this.#runSearch(term), 250);
  }

  /** @param {string} term */
  async #runSearch(term) {
    const list = this.querySelector('[data-quote-list]');
    if (!(list instanceof HTMLElement)) return;

    const empty = this.querySelector('[data-quote-empty]');
    const pagination = this.querySelector('[data-quote-pagination]');
    const status = this.querySelector('[data-quote-search-status]');

    // Any in-flight request is now answering a stale term.
    this.#searchController?.abort();

    if (term.length < 2) {
      this.#restoreCatalogue();
      return;
    }

    const controller = (this.#searchController = new AbortController());
    if (status instanceof HTMLElement) {
      status.hidden = false;
      status.textContent = 'Searching the full catalogue…';
    }
    if (pagination instanceof HTMLElement) pagination.hidden = true;
    if (empty instanceof HTMLElement) empty.hidden = true;

    const base = this.dataset.searchUrl || '/search';
    // The trailing * is what makes Shopify match on a prefix, so "kit" finds
    // "Kits" and "Starter Kit" rather than only an exact token.
    const url = `${base}?q=${encodeURIComponent(`${term}*`)}&type=product&view=quote`;

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`search responded ${response.status}`);
      const html = await response.text();

      list.innerHTML = html.trim();

      // The template carries the true match count, which can exceed what one
      // paginated response returns.
      const meta = list.querySelector('[data-quote-search-meta]');
      const found = list.querySelectorAll('[data-quote-row]').length;
      const total = meta ? Number(meta.getAttribute('data-total')) || found : found;
      meta?.remove();

      if (empty instanceof HTMLElement) {
        empty.textContent = `No products match “${term}”.`;
        empty.hidden = found > 0;
      }
      if (status instanceof HTMLElement) {
        status.hidden = false;
        status.textContent =
          total > found
            ? `Showing the first ${found} of ${total} matches for “${term}”. Add another word to narrow it down`
            : `${found} result${found === 1 ? '' : 's'} for “${term}” across the whole catalogue`;
      }
      this.#syncRows();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      // Degrade to the old behaviour rather than leaving a dead search box.
      this.#restoreCatalogue({ keepStatus: true });
      const shown = this.#filterLocally(term);
      if (status instanceof HTMLElement) {
        status.hidden = false;
        status.textContent = 'Catalogue search is unavailable. Showing matches on this page only.';
      }
      if (empty instanceof HTMLElement) {
        empty.textContent = `No products match “${term}” on this page.`;
        empty.hidden = shown > 0;
      }
    }
  }

  /** @param {{keepStatus?: boolean}} [options] */
  #restoreCatalogue({ keepStatus = false } = {}) {
    const list = this.querySelector('[data-quote-list]');
    const empty = this.querySelector('[data-quote-empty]');
    const pagination = this.querySelector('[data-quote-pagination]');
    const status = this.querySelector('[data-quote-search-status]');

    if (list instanceof HTMLElement && this.#catalogueHTML !== null) {
      list.innerHTML = this.#catalogueHTML;
    }
    if (empty instanceof HTMLElement) empty.hidden = true;
    if (pagination instanceof HTMLElement) pagination.hidden = false;
    if (!keepStatus && status instanceof HTMLElement) status.hidden = true;
    this.#syncRows();
  }

  /**
   * Offline fallback only. Returns how many rows survived the filter.
   * @param {string} term
   */
  #filterLocally(term) {
    const needle = term.toLowerCase();
    let shown = 0;
    for (const row of this.querySelectorAll('[data-quote-row]')) {
      const haystack = (row.getAttribute('data-search') || '').toLowerCase();
      const match = !needle || haystack.includes(needle);
      if (row instanceof HTMLElement) row.hidden = !match;
      if (match) shown += 1;
    }
    return shown;
  }

  /** Paints the indicator and shows exactly one panel. */
  #renderSteps() {
    for (const item of this.querySelectorAll('[data-quote-tab]')) {
      const n = Number(item.getAttribute('data-quote-tab'));
      item.classList.toggle('is-active', n === this.#step);
      item.classList.toggle('is-done', n < this.#step);
    }
    for (const panel of this.querySelectorAll('[data-quote-panel]')) {
      if (panel instanceof HTMLElement) {
        panel.hidden = Number(panel.getAttribute('data-quote-panel')) !== this.#step;
      }
    }
    const bar = this.querySelector('[data-quote-bar]');
    // Persistent on step 2 (not gated on selection): the proceed anchor stays
    // in view whether the quote is empty or not, so no one has to scroll to
    // find it. When empty it shows a prompt; once a line is added, a tally.
    if (bar instanceof HTMLElement) bar.hidden = this.#step !== 2;
  }

  /**
   * @param {number} to
   * Forward moves are gated; going back never is, so a customer can always
   * return to fix something.
   */
  #goto(to) {
    if (to === this.#step) return;

    if (to > this.#step && !this.#validateAll()) {
      this.#step = 1;
      this.#renderSteps();
      const bad = this.querySelector('[data-quote-field][aria-invalid="true"]');
      if (bad instanceof HTMLElement) bad.focus();
      return;
    }
    if (to === 3 && this.#lines.size === 0) return;

    this.#step = to;
    this.#persist();
    this.#renderSteps();
    this.#render();

    // On mobile the catalogue can be scrolled a long way down by the time
    // Review is tapped, so the new panel would otherwise open off-screen.
    const panel = this.querySelector(`[data-quote-panel="${to}"]`);
    if (panel instanceof HTMLElement) panel.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  /** Adds a free-text line for something the catalogue does not carry. */
  #addCustom() {
    const text = this.querySelector('[data-quote-custom-text]');
    const qtyEl = this.querySelector('[data-quote-custom-qty]');
    const error = this.querySelector('[data-quote-custom-error]');
    const value = text instanceof HTMLTextAreaElement ? text.value.trim() : '';

    if (!value) {
      if (error instanceof HTMLElement) {
        error.textContent = 'Describe what you are looking for first.';
        error.hidden = false;
      }
      if (text instanceof HTMLElement) text.focus();
      return;
    }
    if (error instanceof HTMLElement) error.hidden = true;

    const qty = qtyEl instanceof HTMLInputElement ? Math.max(1, parseInt(qtyEl.value, 10) || 1) : 1;
    this.#seq += 1;
    this.#lines.set(`custom:${this.#seq}`, {
      qty,
      title: value,
      variant: '',
      sku: '',
      // No price is invented for these; they are carried through the quote and
      // the PDF as explicitly unpriced.
      price: 0,
      url: '',
      custom: true,
    });

    if (text instanceof HTMLTextAreaElement) text.value = '';
    if (qtyEl instanceof HTMLInputElement) qtyEl.value = '1';
    const form = this.querySelector('[data-quote-custom-form]');
    if (form instanceof HTMLElement) form.hidden = true;

    this.#persist();
    this.#render();

    // Collapsing the form removes the Save button the user just activated, so
    // focus would fall to <body>. Move it to the still-visible opener and
    // announce the result, so a keyboard/AT user learns the item landed and
    // that the (now-enabled) proceed control exists. This is the exact
    // custom-only persona the header/bar changes target.
    const opener = this.querySelector('[data-quote-custom-open]');
    if (opener instanceof HTMLElement) opener.focus();
    const status = this.querySelector('[data-quote-custom-status]');
    if (status instanceof HTMLElement) {
      const n = this.#lines.size;
      status.textContent =
        `Added. ${n} item${n === 1 ? '' : 's'} on your quote. Review quote is now available.`;
    }
  }

  #renderCustom() {
    const list = this.querySelector('[data-quote-custom-list]');
    if (!(list instanceof HTMLElement)) return;
    list.innerHTML = [...this.#lines.entries()]
      .filter(([, line]) => line.custom)
      .map(
        ([id, line]) => `<li class="quote-custom__item">
            <span class="quote-custom__text">${escapeHtml(line.title)}</span>
            <span class="quote-custom__tag">Qty ${line.qty} &middot; to be quoted</span>
            <button type="button" class="quote-custom__remove" data-quote-remove="${escapeHtml(id)}" aria-label="Remove custom item">&times;</button>
          </li>`
      )
      .join('');
  }

  #renderRecap() {
    const el = this.querySelector('[data-quote-recap]');
    if (!(el instanceof HTMLElement)) return;
    const c = this.#customer();
    el.innerHTML = `<strong>${escapeHtml(c.name)}</strong>
      <span>${escapeHtml(c.email)}</span>
      <span>${escapeHtml(c.phone)}</span>
      <button type="button" class="quote-recap__edit" data-quote-goto="1">Edit details</button>`;
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const row = target.closest('[data-quote-row]');
    if (!row) return;

    // Switching variant moves the line rather than adding a second one.
    if (target.matches('[data-quote-variant]')) {
      for (const [id, line] of this.#lines) {
        if (line.rowKey === row.getAttribute('data-row-key')) this.#lines.delete(id);
      }
    }

    const id = this.#variantId(row);
    if (!id) return;
    const toggle = row.querySelector('[data-quote-toggle]');
    const selected = toggle instanceof HTMLInputElement ? toggle.checked : this.#lines.has(id);

    if (selected) {
      const line = this.#readRow(row);
      line.rowKey = row.getAttribute('data-row-key') || '';
      this.#lines.set(id, line);
    } else {
      this.#lines.delete(id);
    }

    row.classList.toggle('is-selected', selected);
    this.#persist();
    this.#render();
  };

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const nav = target.closest('[data-quote-next], [data-quote-back], [data-quote-goto]');
    if (nav instanceof HTMLElement) {
      event.preventDefault();
      const to =
        nav.getAttribute('data-quote-next') ||
        nav.getAttribute('data-quote-back') ||
        nav.getAttribute('data-quote-goto');
      this.#goto(Number(to));
      return;
    }

    if (target.closest('[data-quote-custom-open]')) {
      event.preventDefault();
      const form = this.querySelector('[data-quote-custom-form]');
      if (form instanceof HTMLElement) {
        form.hidden = false;
        this.querySelector('[data-quote-custom-text]')?.focus();
      }
      return;
    }

    if (target.closest('[data-quote-custom-cancel]')) {
      event.preventDefault();
      const form = this.querySelector('[data-quote-custom-form]');
      if (form instanceof HTMLElement) form.hidden = true;
      return;
    }

    if (target.closest('[data-quote-custom-save]')) {
      event.preventDefault();
      this.#addCustom();
      return;
    }

    const step = target.closest('[data-quote-step]');
    if (step instanceof HTMLElement) {
      event.preventDefault();
      const row = step.closest('[data-quote-row]');
      const input = row?.querySelector('[data-quote-qty]');
      if (!(input instanceof HTMLInputElement)) return;
      const delta = step.dataset.quoteStep === 'up' ? 1 : -1;
      input.value = String(Math.max(1, (parseInt(input.value, 10) || 1) + delta));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (target.closest('[data-quote-remove]')) {
      event.preventDefault();
      const id = target.closest('[data-quote-remove]')?.getAttribute('data-quote-remove');
      if (id) {
        this.#lines.delete(id);
        this.#persist();
        this.#syncRows();
        this.#render();
      }
      return;
    }

    if (target.closest('[data-quote-clear]')) {
      event.preventDefault();
      this.#lines.clear();
      this.#persist();
      this.#syncRows();
      this.#render();
      return;
    }

    if (target.closest('[data-quote-print]')) {
      event.preventDefault();
      this.#print();
      return;
    }

    /*
     * Whole-card selection. The checkbox is a ~20px target on a phone, which is
     * a poor thing to ask a thumb to hit, so a tap anywhere on the card toggles
     * it. Anything that already means something else is excluded: the Details
     * link, the quantity stepper, the variant select, and the checkbox and its
     * own label (which the browser already forwards, so handling it here too
     * would toggle twice and cancel out).
     */
    const row = target.closest('[data-quote-row]');
    if (row && !target.closest('a, button, select, input, label')) {
      const toggle = row.querySelector('[data-quote-toggle]');
      if (toggle instanceof HTMLInputElement) {
        toggle.checked = !toggle.checked;
        toggle.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };

  /** @param {Event} event */
  #handleInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.matches('[data-quote-search]')) {
      this.#queueSearch(target.value);
      return;
    }

    if (target.matches('[data-quote-field]')) {
      this.#validateField(target);
      this.#persist();
      this.#render();
    }
  };

  /** @param {HTMLInputElement} field */
  #validateField(field) {
    const wrapper = field.closest('[data-quote-field-wrap]');
    const message = wrapper?.querySelector('[data-quote-error]');
    let error = '';

    const value = field.value.trim();
    if (!value) {
      error = 'Required';
    } else if (field.type === 'email' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      error = 'Enter a valid email address';
    } else if (field.name === 'phone' && value.replace(/[^\d]/g, '').length < 7) {
      error = 'Enter a valid phone number';
    }

    field.setAttribute('aria-invalid', error ? 'true' : 'false');
    if (message instanceof HTMLElement) {
      message.textContent = error;
      message.hidden = !error;
    }
    return !error;
  }

  #validateAll() {
    let ok = true;
    for (const field of this.querySelectorAll('[data-quote-field]')) {
      if (field instanceof HTMLInputElement && !this.#validateField(field)) ok = false;
    }
    return ok;
  }

  #customer() {
    const get = (name) => {
      const el = this.querySelector(`[data-quote-field][name="${name}"]`);
      return el instanceof HTMLInputElement ? el.value.trim() : '';
    };
    return { name: get('name'), email: get('email'), phone: get('phone') };
  }

  #total() {
    let total = 0;
    for (const line of this.#lines.values()) if (!line.custom) total += line.price * line.qty;
    return total;
  }

  #hasCustom() {
    for (const line of this.#lines.values()) if (line.custom) return true;
    return false;
  }

  #reference() {
    if (!this.#ref) {
      const now = new Date();
      const stamp = now.toISOString().slice(0, 10).replace(/-/g, '');
      this.#ref = `Q-${stamp}-${String(now.getTime()).slice(-5)}`;
      this.#persist();
    }
    return this.#ref;
  }

  /** Plain-text quote, used for the email body and as the print fallback. */
  #asText() {
    const c = this.#customer();
    const rows = [...this.#lines.values()].map((l) =>
      l.custom
        ? `- [Custom request] ${l.title} x${l.qty}: to be quoted`
        : `- ${l.title}${l.variant ? ` (${l.variant})` : ''}${l.sku ? ` [${l.sku}]` : ''} x${l.qty}: ${this.#money(l.price * l.qty)}`
    );
    return [
      `Quote request ${this.#reference()}`,
      '',
      `Name: ${c.name}`,
      `Email: ${c.email}`,
      `Phone: ${c.phone}`,
      '',
      'Items:',
      ...rows,
      '',
      `Estimated total: ${this.#money(this.#total())}`,
    ].join('\n');
  }

  #render() {
    const list = this.querySelector('[data-quote-summary]');
    const count = this.querySelector('[data-quote-count]');
    const totalEl = this.querySelector('[data-quote-total]');
    const actions = this.querySelector('[data-quote-actions]');
    const body = this.querySelector('[data-quote-body]');

    if (count) count.textContent = String(this.#lines.size);
    if (totalEl) totalEl.textContent = this.#money(this.#total());
    if (actions instanceof HTMLElement) actions.hidden = this.#lines.size === 0;
    if (body instanceof HTMLTextAreaElement) body.value = this.#asText();

    // Show the quote number on screen so it matches the PDF and emailed copy.
    const refLine = this.querySelector('[data-quote-ref-line]');
    const refEl = this.querySelector('[data-quote-ref]');
    if (refLine instanceof HTMLElement) refLine.hidden = this.#lines.size === 0;
    if (refEl instanceof HTMLElement && this.#lines.size > 0) refEl.textContent = this.#reference();

    const barCount = this.querySelector('[data-quote-bar-count]');
    const barTotal = this.querySelector('[data-quote-bar-total]');
    if (barCount) barCount.textContent = String(this.#lines.size);
    if (barTotal) barTotal.textContent = this.#money(this.#total());

    // Bar shows a prompt while the quote is empty, a tally once it isn't.
    const barPrompt = this.querySelector('[data-quote-bar-prompt]');
    const barTally = this.querySelector('[data-quote-bar-tally]');
    if (barPrompt instanceof HTMLElement) barPrompt.hidden = this.#lines.size !== 0;
    if (barTally instanceof HTMLElement) barTally.hidden = this.#lines.size === 0;

    const note = this.querySelector('[data-quote-custom-note]');
    if (note instanceof HTMLElement) note.hidden = !this.#hasCustom();

    // Nothing to review until something is on the quote.
    const empty = this.#lines.size === 0;
    for (const el of this.querySelectorAll('[data-quote-next="3"]')) {
      if (el instanceof HTMLButtonElement) {
        el.disabled = empty;
      } else if (el instanceof HTMLElement) {
        // The row's proceed control is an <a> (shiny button) - anchors can't be
        // :disabled, so mirror the state with a class, ARIA, and tab removal.
        el.classList.toggle('is-disabled', empty);
        el.setAttribute('aria-disabled', String(empty));
        if (empty) el.setAttribute('tabindex', '-1');
        else el.removeAttribute('tabindex');
      }
    }
    // The proceed hint is the inverse of the button: it only tells the customer
    // what to do while the button is disabled.
    for (const hint of this.querySelectorAll('[data-quote-proceed-hint]')) {
      if (hint instanceof HTMLElement) hint.hidden = !empty;
    }

    const bar = this.querySelector('[data-quote-bar]');
    // Persistent on step 2 (not gated on selection): the proceed anchor stays
    // in view whether the quote is empty or not, so no one has to scroll to
    // find it. When empty it shows a prompt; once a line is added, a tally.
    if (bar instanceof HTMLElement) bar.hidden = this.#step !== 2;

    this.#renderCustom();
    this.#renderRecap();

    // Shopify's contact form reads contact[...] fields, not ours. Mirroring on
    // every render means a plain (non-JS-intercepted) POST still carries the
    // customer's details even though the visible inputs are named differently.
    const c = this.#customer();
    for (const mirror of this.querySelectorAll('[data-quote-mirror]')) {
      if (mirror instanceof HTMLInputElement) mirror.value = c[mirror.dataset.quoteMirror] || '';
    }

    if (!(list instanceof HTMLElement)) return;
    if (this.#lines.size === 0) {
      list.innerHTML = '<li class="quote-summary__empty">No products selected yet.</li>';
      return;
    }

    list.innerHTML = [...this.#lines.entries()]
      .map(([id, l]) => {
        const name = [l.title, l.variant].filter(Boolean).join(' · ');
        const amount = l.custom ? 'To be quoted' : this.#money(l.price * l.qty);
        return `<li class="quote-summary__line">
            <span class="quote-summary__name">${escapeHtml(name)}</span>
            <span class="quote-summary__qty">x${l.qty}</span>
            <span class="quote-summary__price">${escapeHtml(amount)}</span>
            <button type="button" class="quote-summary__remove" data-quote-remove="${escapeHtml(id)}" aria-label="Remove ${escapeHtml(name)}">&times;</button>
          </li>`;
      })
      .join('');
  }

  /**
   * Composes the printable quote and opens the print dialogue. The document is
   * built into a container that only `@media print` reveals, so nothing about
   * the on-screen page shifts.
   */
  #print() {
    if (!this.#validateAll()) {
      this.querySelector('[data-quote-field][aria-invalid="true"]')?.focus();
      return;
    }
    if (this.#lines.size === 0) return;

    // Print from an isolated iframe. The previous approach printed the live
    // page and relied on an @media print visibility-flip to hide the storefront
    // around the quote; that let the whole page (header, mega-menu, collections)
    // through. An iframe carries only the document we write into it, so nothing
    // else can leak into the PDF.
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';

    const remove = () => {
      if (frame.parentNode) frame.parentNode.removeChild(frame);
    };

    let printed = false;
    frame.addEventListener('load', () => {
      const win = frame.contentWindow;
      // Ignore any empty pre-content load; only print once the doc is in place.
      if (printed || !win || !win.document.querySelector('.doc')) return;
      printed = true;
      win.addEventListener('afterprint', remove, { once: true });
      win.setTimeout(remove, 60000); // fallback for browsers that skip afterprint
      win.focus();
      win.print();
    });

    frame.srcdoc = this.#quoteDocument();
    document.body.appendChild(frame);
  }

  /**
   * A complete, self-contained HTML quotation - content and styling both in the
   * returned string - so it renders identically inside the print iframe with no
   * theme CSS in scope. Always carries a "this is a quote, not an invoice"
   * disclaimer; the shop's configurable terms follow as fine print.
   */
  #quoteDocument() {
    const c = this.#customer();
    const date = new Date().toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const ref = this.#reference();
    const bizName = this.dataset.bizName || '';
    const bizEmail = this.dataset.bizEmail || '';
    const bizPhone = this.dataset.bizPhone || '';
    const bizAddress = this.dataset.bizAddress || '';
    const terms = this.dataset.terms || '';

    // Address (one line per row) then phone and email, each on its own line.
    const contact = [
      ...bizAddress.split('\n').map((l) => l.trim()),
      bizPhone,
      bizEmail,
    ]
      .filter(Boolean)
      .map((l) => `<p>${escapeHtml(l)}</p>`)
      .join('');

    const rows = [...this.#lines.values()]
      .map(
        (l) => `<tr>
          <td class="item">${escapeHtml([l.title, l.variant].filter(Boolean).join(' · '))}${
          l.custom ? ' <span class="tag">Custom request</span>' : ''
        }</td>
          <td>${escapeHtml(l.sku || '')}</td>
          <td class="num">${l.qty}</td>
          <td class="num">${l.custom ? '&mdash;' : escapeHtml(this.#money(l.price))}</td>
          <td class="num">${l.custom ? 'To be quoted' : escapeHtml(this.#money(l.price * l.qty))}</td>
        </tr>`
      )
      .join('');

    const pending = this.#hasCustom()
      ? `<p class="pending">Items marked &ldquo;To be quoted&rdquo; are custom requests and are <strong>not</strong> included in the estimated total; they are priced separately.</p>`
      : '';

    return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Quotation ${escapeHtml(ref)}</title>
<style>
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font: 11pt/1.5 -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #111; background: #fff;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  /* Page margins live on the document (not @page) so the browser's own
     header/footer band - the date, title and page URL - has no margin to draw
     into and disappears, leaving just the quotation. */
  .doc { padding: 16mm 16mm 18mm; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 24pt; padding-bottom: 12pt; border-bottom: 2pt solid #111; }
  .shop { margin: 0; font-size: 20pt; font-weight: 800; letter-spacing: -0.01em; }
  .shop-contact { margin: 6pt 0 0; color: #555; font-size: 9pt; }
  .shop-contact p { margin: 0; line-height: 1.45; }
  .ref { text-align: right; white-space: nowrap; }
  .ref .kicker { margin: 0 0 5pt; font-size: 13pt; font-weight: 700; letter-spacing: 0.14em; }
  .ref p { margin: 0; font-size: 10pt; }
  .ref .label { color: #777; }
  .to { padding: 14pt 0 6pt; }
  .to .label { margin: 0 0 3pt; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.1em; color: #777; }
  .to p { margin: 0; }
  table { width: 100%; border-collapse: collapse; margin-top: 8pt; }
  th { text-align: left; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.06em; color: #555; border-bottom: 1pt solid #111; padding: 6pt 4pt; }
  td { padding: 7pt 4pt; border-bottom: 0.5pt solid #ccc; vertical-align: top; }
  td.item { width: 46%; }
  .num { text-align: right; white-space: nowrap; }
  tfoot td { border-bottom: none; border-top: 1.5pt solid #111; padding-top: 9pt; font-size: 12pt; }
  .tag { display: inline-block; margin-left: 4pt; padding: 1pt 5pt; font-size: 7.5pt; text-transform: uppercase; letter-spacing: 0.05em; border: 0.75pt solid #999; border-radius: 3pt; color: #555; }
  .pending { margin: 10pt 0 0; font-size: 9.5pt; color: #444; }
  .disclaimer { margin-top: 16pt; padding: 10pt 12pt; border: 1.25pt solid #111; border-radius: 4pt; }
  .disclaimer p { margin: 0 0 5pt; font-size: 9.5pt; }
  .disclaimer p:last-child { margin-bottom: 0; }
  .disclaimer strong { font-size: 10.5pt; }
  .terms { margin-top: 12pt; font-size: 8.5pt; color: #666; white-space: pre-line; }
  @page { margin: 0; }
</style>
</head>
<body>
  <main class="doc">
    <header class="head">
      <div class="brand">
        <p class="shop">${escapeHtml(bizName)}</p>
        ${contact ? `<div class="shop-contact">${contact}</div>` : ''}
      </div>
      <div class="ref">
        <p class="kicker">QUOTATION</p>
        <p><span class="label">No.</span> ${escapeHtml(ref)}</p>
        <p><span class="label">Date</span> ${escapeHtml(date)}</p>
      </div>
    </header>

    <section class="to">
      <p class="label">Prepared for</p>
      <p>${escapeHtml(c.name)}</p>
      ${c.email ? `<p>${escapeHtml(c.email)}</p>` : ''}
      ${c.phone ? `<p>${escapeHtml(c.phone)}</p>` : ''}
    </section>

    <table>
      <thead>
        <tr><th>Item</th><th>SKU</th><th class="num">Qty</th><th class="num">Unit</th><th class="num">Amount</th></tr>
      </thead>
      <tbody>${rows}</tbody>
      <tfoot>
        <tr><td colspan="4" class="num"><strong>Estimated total</strong></td>
            <td class="num"><strong>${escapeHtml(this.#money(this.#total()))}</strong></td></tr>
      </tfoot>
    </table>

    ${pending}

    <section class="disclaimer">
      <p><strong>This is a quotation, not a tax invoice or a confirmed order.</strong></p>
      <p>The prices above are estimates for the items listed and are subject to stock availability and to final written confirmation by ${escapeHtml(bizName || 'us')}. This document does not constitute a sale, a binding agreement, or a request for payment.</p>
    </section>

    ${terms ? `<p class="terms">${escapeHtml(terms)}</p>` : ''}
  </main>
</body>
</html>`;
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-quote-form]')) return;

    if (this.#step !== 3 || !this.#validateAll() || this.#lines.size === 0) {
      event.preventDefault();
      this.querySelector('[data-quote-field][aria-invalid="true"]')?.focus();
      return;
    }

    const endpoint = this.dataset.quoteEndpoint;
    if (!endpoint) return; // fall through to the Shopify contact POST

    // A custom endpoint takes over: send structured JSON so the service can
    // build and mail the PDF itself.
    event.preventDefault();
    const payload = {
      reference: this.#reference(),
      customer: this.#customer(),
      currency: this.#currency,
      lines: [...this.#lines.values()],
      total: this.#total(),
    };
    const status = this.querySelector('[data-quote-status]');
    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (status instanceof HTMLElement) {
          status.hidden = false;
          status.textContent = res.ok
            ? 'Thanks, your quote is on its way by email.'
            : 'Something went wrong sending your quote. Please try downloading it instead.';
        }
      })
      .catch(() => {
        if (status instanceof HTMLElement) {
          status.hidden = false;
          status.textContent = 'Could not reach the quote service. Please try downloading it instead.';
        }
      });
  };
}

/** @param {string} value */
function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (ch) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[ch]));
}

if (!customElements.get('detailer-quote')) {
  customElements.define('detailer-quote', DetailerQuote);
}
